import store from '@/store';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';

// Columns stored per section on the profile: column key -> path under sections.<id>.
const SECTION_COLUMN_PATHS = {
  status: 'status',
  retake: 'retake',
  makeup: 'makeup.enabled',
  toc: 'toc.enabled',
  examVoucher: 'examVoucher',
  examTaken: 'examTaken',
  examPassed: 'examPassed',
  labs: 'labCompleted',
};

const readPath = (object, path) => {
  const step = (value, part) => (value === null || value === undefined ? undefined : value[part]);
  return path.split('.').reduce(step, object);
};

/**
 * Shared behaviour for tables whose rows are one student's sections, each row a
 * section with the roster profile at `row.profile` (student profile, pathway
 * and course cohort section lists).
 *
 * The component must render its b-table with ref="refProfileListTable", and
 * the parent list listens for `add-credit` (section id) -- credit needs a note,
 * so it opens the Add Credit form -- and `changed`.
 */
export default {
  methods: {
    sectionOf(item) {
      return item && item.profile && item.profile.sections ? item.profile.sections[item.id] : undefined;
    },
    sectionValue(item, key) {
      const section = this.sectionOf(item);
      return section ? readPath(section, SECTION_COLUMN_PATHS[key] || key) : undefined;
    },
    /**
     * Sorts section columns in the browser. These rows come from an endpoint
     * that sorts Section documents, which do not carry the per-profile values,
     * and a student has only a handful of sections. Other columns fall back to
     * the default comparison.
     */
    sectionSortCompare(aRow, bRow, key) {
      if (!SECTION_COLUMN_PATHS[key]) return null;
      const a = this.sectionValue(aRow, key);
      const b = this.sectionValue(bRow, key);
      return String(a === undefined ? '' : a).localeCompare(String(b === undefined ? '' : b));
    },
    refreshSections() {
      if (this.$refs.refProfileListTable) this.$refs.refProfileListTable.refresh();
    },
    notifySectionFailure(title, error) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: 'AlertTriangleIcon',
          variant: 'danger',
          text: error && error.response && error.response.data ? error.response.data.message : error && error.message,
        },
      });
    },
    sectionMakeupEnabled(item) {
      return !!this.sectionValue(item, 'makeup');
    },
    sectionCreditEnabled(item) {
      return !!this.sectionValue(item, 'toc');
    },
    setSectionMakeup(item, enabled) {
      store
        .dispatch('rosterStoreModule/assignCourseMakeup', { profileId: item.profile.id, sectionId: item.id, enabled })
        .then(() => this.refreshSections())
        .catch((error) => this.notifySectionFailure('Failed to update makeup', error));
    },
    /** Yes asks the parent list to open Add Credit on this section; No removes it after confirming. */
    setSectionCredit(item, enabled) {
      if (enabled) {
        this.$emit('add-credit', item.id);
        return;
      }

      this.$swal({
        title: 'Remove credit?',
        text: `Remove the transfer of credit for ${item.name}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Remove',
        customClass: { confirmButton: 'btn btn-primary', cancelButton: 'btn btn-outline-danger ml-1' },
        buttonsStyling: false,
      }).then((result) => {
        if (!result.value) return;
        store
          .dispatch('rosterStoreModule/unassignCourseCredit', {
            id: item.profile.id,
            creditData: { enabled: false, sectionId: item.id },
          })
          .then(() => {
            this.refreshSections();
            this.$emit('changed');
          })
          .catch((error) => this.notifySectionFailure('Failed to remove credit', error));
      });
    },
  },
};
